import React, { PropsWithChildren, useEffect, useState } from "react"
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Switch, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { FormCard } from "./FormCard"

const useStyles = makeStyles()((theme) => ({
    accordion: {
        border: `1px solid ${theme.palette.tailwind.slate[100]}`,
        borderRadius: ".75rem !important",
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        "&::before": {
            display: "none",
        },
    },
    accordionDetails: {
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
        padding: "1.75rem 2rem",
    },
    boxTitle: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[600],
    },
    defaultTitle: {
        marginBottom: "2rem",
    },
    accordionTitle: {
        paddingLeft: "1rem",
    },
    offContent: {
        textAlign: "center",
        color: theme.palette.tailwind.slate[400],
        fontStyle: "italic",
    },
    switchableHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    switchableHeaderUpdate: {
        padding: "0.5rem 1.25rem",
    },
    switchableBox: {
        padding: "1.75rem 1.25rem",
    },
    noBorder: {
        border: "none",
        boxShadow: "none",
    },
}))

type DefaultProps = {
    variant?: "default"
    title?: string
    checked?: boolean
    onCheckChange?: (checked: boolean) => void
    offContent?: string | React.ReactNode
    hideContentBox?: boolean
    hideBorder?: boolean
    spacing?: string
}

type CollapsibleProps = {
    variant: "collapsible"
    title: string
    checked?: boolean
    onCheckChange?: (checked: boolean) => void
    offContent?: string | React.ReactNode
    hideContentBox?: boolean
    hideBorder?: boolean
    spacing?: string
}

type SwitchableProps = {
    variant: "switchable"
    title: string
    checked: boolean
    onCheckChange?: (checked: boolean) => void
    offContent?: string | React.ReactNode
    hideContentBox?: boolean
    hideBorder?: boolean
    size?: "half"
    spacing?: string
}

type StandardMainPanelProps = PropsWithChildren<DefaultProps | CollapsibleProps | SwitchableProps>

export function StandardMainPanel({
    children,
    title,
    variant = "default",
    checked = false,
    onCheckChange,
    offContent,
    hideBorder,
    hideContentBox = false,
    spacing = "1.25rem",
}: StandardMainPanelProps) {
    const { classes, cx } = useStyles()
    const [isChecked, setIsChecked] = useState(checked)

    useEffect(() => {
        setIsChecked(checked)
    }, [checked])

    const renderChildren = () => (
        <Stack direction="column" spacing={spacing}>
            {children}
        </Stack>
    )

    const renderOffContent = () => (
        <Box>
            {typeof offContent === "string" ? (
                <Typography className={classes.offContent}>{offContent}</Typography>
            ) : (
                offContent
            )}
        </Box>
    )

    if (variant === "default") {
        return (
            <FormCard className={cx(hideBorder && classes.noBorder)}>
                {title && (
                    <Typography variant="subtitle1" className={cx(classes.boxTitle, classes.defaultTitle)}>
                        {title}
                    </Typography>
                )}
                {renderChildren()}
            </FormCard>
        )
    }

    if (variant === "switchable") {
        return (
            <FormCard isPadding={false}>
                <Box className={cx(classes.switchableHeader, classes.switchableHeaderUpdate)}>
                    <Typography variant="subtitle1" className={classes.boxTitle}>
                        {title}
                    </Typography>
                    <Switch
                        checked={isChecked}
                        onChange={(_, state) => {
                            setIsChecked(state)
                            onCheckChange?.(state)
                        }}
                    />
                </Box>
                <Box className={cx(!hideContentBox && classes.switchableBox)}>
                    {!isChecked && offContent ? renderOffContent() : renderChildren()}
                </Box>
            </FormCard>
        )
    }

    return (
        <Accordion defaultExpanded elevation={0} className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" className={cx(classes.boxTitle, classes.accordionTitle)}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>{renderChildren()}</AccordionDetails>
        </Accordion>
    )
}
